import { LocationType } from '@prisma/client';
import { TranslocoService } from '@jsverse/transloco';

import { CapitalizePipe } from '../pipes';

/**
 * 🔴 **Chave de i18n por tipo de location, EXAUSTIVA de propósito.**
 *
 * `Record<LocationType, string>` e não ternário: o ternário `type === 'ROOM' ? sala : mesa` compila
 * liso quando o enum cresce, e o valor novo sai rotulado como o ramo de fallback — foi assim que a
 * F14 encontrou a comanda sendo chamada de "Mesa" em dez lugares. Com o `Record`, acrescentar valor
 * ao `LocationType` **quebra o build** aqui e em todo mapa irmão, que é onde o erro tem de aparecer.
 *
 * As chaves são minúsculas no i18n de propósito (o painel usa `mesa`/`todos` em selects), então a
 * capitalização fica com o consumidor — ver `locationTypeLabel`.
 */
export const LOCATION_TYPE_I18N_KEY: Record<LocationType, string> = {
  TABLE: 'location-type-table',
  ROOM: 'location-type-room',
  TAB: 'location-type-tab',
};

/**
 * Lista estável para montar select e filtro, na ordem de exibição.
 *
 * Derivada das chaves do mapa acima, não escrita à mão: é o que faz um tipo novo aparecer no cadastro
 * e no filtro sem ninguém lembrar de acrescentá-lo em três componentes. A ordem é a de declaração do
 * `Record` — do mais comum para o menos.
 */
export const LOCATION_TYPES = Object.keys(LOCATION_TYPE_I18N_KEY) as LocationType[];

/**
 * A mesma implementação que os templates aplicam, reusada em TS. Não vem de `@hmax-order/shared/format`
 * de propósito: dezenas de specs do repo fazem `vi.mock('@hmax-order/shared/format', () => ({}))`, e
 * qualquer código de produção que dependa dela quebra nesses testes com um erro que não fala do
 * defeito real.
 */
const capitalize = new CapitalizePipe();

/**
 * Rótulo do tipo da location (`Mesa`/`Acomodação`/`Comanda`) já capitalizado.
 *
 * O texto sempre aparece composto — `101 · Acomodação`, na coluna da lista, no cabeçalho do detalhe e
 * nas opções dos diálogos —, e nessa posição nenhum pipe de template alcança as duas metades.
 *
 * A função existe porque este ternário estava copiado em quatro lugares e só um deles capitalizava.
 * Mudou de lib na F14 (era de `frontend-pdv-account`): passou a ser consumida também pelo cadastro de
 * locations, pela home e pelo cardápio, e util de quatro features mora na lib compartilhada.
 *
 * `type` continua `string | null | undefined` porque as projeções da API o entregam assim; valor que
 * não casa nenhum membro do enum — location legada, ou tipo removido — cai em **mesa**, que é o
 * default histórico desta função e o tipo mais comum do cadastro.
 */
export function locationTypeLabel(translocoService: TranslocoService, type: string | null | undefined): string {
  const key = LOCATION_TYPE_I18N_KEY[type as LocationType] ?? LOCATION_TYPE_I18N_KEY.TABLE;
  return capitalize.transform(translocoService.translate(key));
}
